import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const LETTER_WIDTH_IN = 8.5;
const LETTER_HEIGHT_IN = 11;
const PAGE_WIDTH_PT = LETTER_WIDTH_IN * 72;
const PAGE_HEIGHT_PT = LETTER_HEIGHT_IN * 72;
const CAPTURE_WIDTH_PX = 816;
const CAPTURE_HEIGHT_PX = 1056;
const PDF_FONT_SIZE_PX = 18;
const PDF_MIN_FONT_SIZE_PX = 12;
const PDF_FONT_SIZE_STEP_PX = 0.25;
const PDF_LINE_HEIGHT = 1.5;
const PDF_PARAGRAPH_GAP_EM = 0.8;

export async function generateLetterPdfBase64(letterElement: HTMLElement): Promise<string> {
  const template = findTemplateElement(letterElement);
  await ensureImagesLoaded(template);

  const canvas = await html2canvas(template, {
    backgroundColor: null,
    scale: 3,
    useCORS: true,
    allowTaint: true,
    onclone: (clonedDocument, clonedElement) => {
      // html2canvas waits for clonedDocument.fonts.ready before invoking this
      // callback. Fit only the clone so the responsive preview remains intact.
      prepareClonedTemplate(clonedElement);
    },
  });

  const pdf = new jsPDF({
    orientation: "portrait",
    unit: "pt",
    format: [PAGE_WIDTH_PT, PAGE_HEIGHT_PT],
    compress: true,
  });

  const imgData = canvas.toDataURL("image/png");
  pdf.addImage(imgData, "PNG", 0, 0, PAGE_WIDTH_PT, PAGE_HEIGHT_PT);

  const pdfBlob = pdf.output("blob");
  return blobToBase64(pdfBlob);
}

function findTemplateElement(element: HTMLElement): HTMLElement {
  if (element.classList.contains("elf-letter-template")) {
    return element;
  }

  const template = element.querySelector<HTMLElement>(".elf-letter-template");
  if (!template) {
    throw new Error("Letter template element not found for PDF generation.");
  }
  return template;
}

async function ensureImagesLoaded(root: HTMLElement): Promise<void> {
  const images = Array.from(root.querySelectorAll("img"));
  if (images.length === 0) {
    return;
  }

  await Promise.all(
    images.map((img) => {
      if (img.complete) {
        if (img.naturalWidth > 0) {
          return Promise.resolve();
        }

        return Promise.reject(
          new Error("The letter template image failed to load."),
        );
      }

      return new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () =>
          reject(new Error("The letter template image failed to load."));
      });
    }),
  );
}

function prepareClonedTemplate(template: HTMLElement): void {
  if (!template.classList.contains("elf-letter-template")) {
    throw new Error("Letter template clone not found for PDF generation.");
  }

  template.style.width = `${CAPTURE_WIDTH_PX}px`;
  template.style.height = `${CAPTURE_HEIGHT_PX}px`;
  template.style.maxWidth = "none";
  template.style.maxHeight = "none";
  template.style.boxSizing = "border-box";

  const text = template.querySelector<HTMLElement>(".elf-letter-text");
  if (!text) {
    throw new Error("Letter text clone not found for PDF generation.");
  }

  text.style.fontSize = `${PDF_FONT_SIZE_PX}px`;
  text.style.lineHeight = `${PDF_LINE_HEIGHT}`;
  text.style.gap = `${PDF_PARAGRAPH_GAP_EM}em`;
  text.style.overflow = "hidden";
  text.scrollTop = 0;
  text.scrollLeft = 0;

  for (
    let fontSize = PDF_FONT_SIZE_PX;
    fontSize >= PDF_MIN_FONT_SIZE_PX;
    fontSize -= PDF_FONT_SIZE_STEP_PX
  ) {
    text.style.fontSize = `${fontSize}px`;
    if (!hasOverflow(text)) {
      return;
    }
  }

  throw new Error(
    "This letter is too long to fit on one printable page. Please shorten it and try again.",
  );
}

function hasOverflow(element: HTMLElement): boolean {
  return (
    element.scrollHeight > element.clientHeight + 1 ||
    element.scrollWidth > element.clientWidth + 1
  );
}

async function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== "string") {
        reject(new Error("Failed to convert PDF blob to base64."));
        return;
      }

      const prefix = "base64,";
      const index = result.indexOf(prefix);
      resolve(index >= 0 ? result.slice(index + prefix.length) : result);
    };
    reader.readAsDataURL(blob);
  });
}

